import { Box } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useReward } from "api/rewards";
import { useAppSelector } from "app/hooks";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { createHeadItems, OLTable, Row } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import {
  ModalMode,
  RewardRedemptionModals,
} from "components/Tooltips/RewardRedemptionModals";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { SINGLE_REWARD_FULFILLMENTS_LIST_ID } from "state/tables/types";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListingActions, ListResponse } from "types/listing";
import { Coupon } from "types/rewards";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    sectionHeader: {
      color: theme.gray800,
      fontSize: 18,
      lineHeight: theme.spacing(11),
      marginBottom: theme.spacing(5),
    },
    filtersContainer: {
      margin: theme.spacing(0, -12),
      padding: theme.spacing(5, 12),
    },
  }),
);

function CreateItems(items: any[]): Row[] {
  const { message } = useMessage();
  return items.map((item) => ({
    cells: [
      { content: item.issuedRewardId, property: "uniqueIdentifier" },
      { content: OLDate(item.redemptionDate), property: "redemptionDate" },
      {
        content: item.customerData?.firstName,
        property: "customerData:firstName",
        dataHjSuppress: true,
      },
      {
        content: item.customerData?.lastName,
        property: "customerData:lastName",
        dataHjSuppress: true,
      },
      {
        content: item.customerData?.email,
        property: "customerData:email",
        dataHjSuppress: true,
      },
      {
        content: item.customerData?.phone,
        property: "customerData:phone",
        dataHjSuppress: true,
      },
      {
        content: item.customerData?.loyaltyCardNumber,
        property: "customerData:loyaltyCardNumber",
        dataHjSuppress: true,
      },
      { content: message({ id: item.status }), property: "status" },
    ],
    id: item.issuedRewardId,
    metadata: {
      issuedCoupon: item.issuedCoupon,
      memberId: item.customerId,
    },
  }));
}

interface SingleRewardFulfillmentsListProps {
  rewardId: string;
}

export function SingleRewardFulfillmentsList({
  rewardId,
}: SingleRewardFulfillmentsListProps) {
  const { message } = useMessage();
  const classes = useStyles();
  const navigate = useNavigate();
  const { listing, updateListing } = useListing(
    SINGLE_REWARD_FULFILLMENTS_LIST_ID,
  );
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();
  const tables = useAppSelector(selectTables);

  const [open, setOpen] = useState(false);
  const [selectedModal, setModal] = useState<{
    issuedCoupon?: Coupon;
    issuedReward?: string;
    memberId?: string;
    mode: number;
  }>();
  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[SINGLE_REWARD_FULFILLMENTS_LIST_ID]?.filters || [],
  );
  const [totalListNumber, setTotalListNumber] = useState(0);

  const setSelectedFilters = (
    filters: React.SetStateAction<SelectedFilterV2[]>,
  ) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      SINGLE_REWARD_FULFILLMENTS_LIST_ID,
    );

    setFilters(filters);
  };

  const { useRewardsFulfillmentList } = useReward();
  const { data, error } = useRewardsFulfillmentList({
    filters: [
      {
        condition: "eq",
        query: "rewardId",
        value: rewardId,
      },
      ...parseFiltersForBE(getNewFiltersOnly(filters)),
    ],
    order: listing.order || "desc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "redemptionDate",
  }) as { data: ListResponse; error?: any };

  const { data: initialData } = useRewardsFulfillmentList({
    filters: [
      {
        condition: "eq",
        query: "rewardId",
        value: rewardId,
      },
    ],
  }) as { data: ListResponse; error?: any };

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && data) {
      setPersistentFilters(filters, SINGLE_REWARD_FULFILLMENTS_LIST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, data, error]);

  useEffect(() => {
    if (initialData) {
      setTotalListNumber(initialData.items.length);
    }
  }, [initialData]);

  const headItems = useMemo(
    () =>
      createHeadItems([
        {
          headerText: message({ id: "redeemedRewardId" }),
          property: "uniqueIdentifier",
          hasMinWidthThreshold: true,
          isSortable: false,
        },
        {
          headerText: message({ id: "redemptionDate" }),
          property: "redemptionDate",
          hasMinWidthThreshold: true,
          isSortable: true,
        },
        {
          headerText: message({ id: "firstName" }),
          property: "customerData:firstName",
          isSortable: true,
        },
        {
          headerText: message({ id: "lastName" }),
          property: "customerData:lastName",
          isSortable: true,
        },
        {
          headerText: message({ id: "email" }),
          property: "customerData:email",
          hasMinWidthThreshold: true,
          isSortable: true,
        },
        {
          headerText: message({ id: "phoneNumber" }),
          property: "customerData:phone",
          isSortable: true,
        },
        {
          headerText: message({ id: "loyaltyCardNumber" }),
          property: "customerData:loyaltyCardNumber",
          isSortable: true,
        },
        {
          headerText: message({ id: "status" }),
          property: "status",
          isSortable: true,
        },
      ]),
    [message],
  );

  const items = data?.items ? CreateItems(data.items) : [];

  const dataTestId = "single-reward-fulfillment";
  return (
    <>
      <div className={classes.sectionHeader}>
        {message({ id: "rewardsFulfillment" })}
      </div>
      <Box className={classes.filtersContainer}>
        <FiltersV2
          data-test-id={dataTestId}
          donutLabel={message({ id: "results" })}
          properties={[
            {
              query: "customerData:email",
              label: message({ id: "email" }),
              type: "member-email-filter",
            },
            {
              query: "customerData:firstName",
              label: message({ id: "firstName" }),
              type: "text",
            },
            {
              query: "customerData:lastName",
              label: message({ id: "lastName" }),
              type: "text",
            },
            {
              query: "customerData:loyaltyCardNumber",
              label: message({ id: "loyaltyCardNumber" }),
              type: "member-loyalty-card-number",
            },
            {
              query: "customerData:phone",
              label: message({ id: "phoneNumber" }),
              type: "member-phone-filter",
            },
            {
              query: "redemptionDate",
              label: message({ id: "redemptionDate" }),
              type: "datetime",
            },
            {
              query: "status",
              label: message({ id: "status" }),
              type: "reward-fulfillment-status",
            },
          ]}
          setFilters={setSelectedFilters}
          savedFilters={getNewFiltersOnly(filters)}
          total={{
            all: totalListNumber,
            estimated: data?.total?.estimated,
            filtered: data?.total?.filtered,
          }}
        />
      </Box>
      <OLTable
        setColumnOrdering={setColumnOrdering}
        tableName={SINGLE_REWARD_FULFILLMENTS_LIST_ID}
        data-test-id={dataTestId}
        onRowClick={(id: string) => {
          navigate(links.rewardsFulfillment(id as string));
        }}
        actions={{
          body: [
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setOpen(true);
                setModal({
                  issuedReward: id as string,
                  mode: ModalMode.ChangeStatus,
                });
              },
              label: message({ id: "changeStatus" }),
            },
            {
              label: message({ id: "markAsUnused" }),
              action: (_, item?: Row) => {
                setOpen(true);
                setModal({
                  issuedCoupon: item?.metadata?.issuedCoupon,
                  memberId: item?.metadata?.memberId,
                  mode: ModalMode.ReissueAssignedCoupon,
                });
              },
              hideCondition: (item: Row) =>
                !item.metadata.issuedCoupon ||
                !item.metadata.issuedCoupon.usedAt,
            },
            {
              label: message({ id: "markAsUsed" }),
              action: (_, item?: Row) => {
                setOpen(true);
                setModal({
                  issuedCoupon: item?.metadata?.issuedCoupon,
                  memberId: item?.metadata?.memberId,
                  mode: ModalMode.RedeemAssignedCoupon,
                });
              },
              hideCondition: (item: Row) =>
                !item.metadata.issuedCoupon ||
                !!item.metadata.issuedCoupon.usedAt,
            },
          ],
        }}
        sort={{
          order: listing.order || "desc",
          property: listing.property || "redemptionDate",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        columns={data && headItems}
        rows={data && items}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(data?.total.filtered, data?.total.all),
          onChangePage: (page) =>
            updateListing({ payload: page, type: ListingActions.CHANGE_PAGE }),
          onChangeRowsPerPage: (rows) =>
            updateListing({
              payload: rows,
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
            }),
          page: listing.page,
          rowsPerPage: listing.rowsPerPage,
        }}
      />
      <RewardRedemptionModals
        close={() => setOpen(false)}
        open={open}
        selectedModal={selectedModal}
      />
    </>
  );
}
